import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { usersApi } from '../../services/api/users.api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`users-tabpanel-${index}`}
      aria-labelledby={`users-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const UsersApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [firstUserId, setFirstUserId] = useState<string | null>(null);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        [name]: { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Управление пользователями
  const testGetUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await usersApi.getUsers();
      setResults(prev => ({ ...prev, 'Get Users': { success: true, data: result } }));
      // Сохраняем ID первого пользователя для тестирования Get User
      // Проверяем разные возможные структуры ответа
      let usersArray = null;
      if (Array.isArray(result)) {
        usersArray = result;
      } else if (result && typeof result === 'object' && 'data' in result && Array.isArray((result as any).data)) {
        usersArray = (result as any).data;
      } else if (result && typeof result === 'object' && 'users' in result && Array.isArray((result as any).users)) {
        usersArray = (result as any).users;
      }
      
      if (usersArray && usersArray.length > 0) {
        setFirstUserId(usersArray[0].id);
      }
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        'Get Users': { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`Get Users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const testGetUser = async () => {
    if (!firstUserId) {
      setError('Сначала выполните тест Get Users для получения ID пользователя');
      return;
    }
    await testEndpoint('Get User', () => usersApi.getUser(firstUserId));
  };
  
  const testGetUserStats = () => testEndpoint('Get User Stats', () => usersApi.getUserStats());

  // Профиль пользователя
  const testGetMyProfile = () => testEndpoint('Get My Profile', () => usersApi.getMyProfile());
  const testGetMySettings = () => testEndpoint('Get My Settings', () => usersApi.getMySettings());
  const testGetMyActivity = () => testEndpoint('Get My Activity', () => usersApi.getMyActivity());

  // Поиск и статистика
  const testSearchUsers = () => testEndpoint('Search Users', () => usersApi.searchUsers('test'));
  const testGetDepartments = () => testEndpoint('Get Departments', () => usersApi.getDepartments());
  const testGetRoles = () => testEndpoint('Get Roles', () => usersApi.getRoles());

  // Экспорт/Импорт
  const testExportUsersCsv = () => testEndpoint('Export Users CSV', () => usersApi.exportUsersCsv());
  const testExportUsersJson = () => testEndpoint('Export Users JSON', () => usersApi.exportUsersJson());

  const testAllManagementEndpoints = async () => {
    await testGetUsers();
    await testGetUser(); // Теперь использует реальный ID пользователя
    await testGetUserStats();
    await testGetDepartments();
    await testGetRoles();
  };

  const testAllProfileEndpoints = async () => {
    await testGetMyProfile();
    await testGetMySettings();
    await testGetMyActivity();
  };

  const testAllExportEndpoints = async () => {
    await testExportUsersCsv();
    await testExportUsersJson();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearResults = () => {
    setResults({});
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        👥 Тестирование Users API
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование 25 endpoints из Users модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="users test tabs">
          <Tab label="Управление пользователями" />
          <Tab label="Профиль пользователя" />
          <Tab label="Поиск и статистика" />
          <Tab label="Экспорт/Импорт" />
          <Tab label="Все результаты" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllManagementEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints управления
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetUsers}
              disabled={loading}
            >
              Тест Get Users
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetUser}
              disabled={loading}
            >
              Тест Get User
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetUserStats}
              disabled={loading}
            >
              Тест User Stats
            </Button>
          </Grid>
        </Grid>

        {/* Отображение результатов для вкладки управления */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(results).filter(([name]) => 
            ['Get Users', 'Get User', 'Get User Stats', 'Get Departments', 'Get Roles'].includes(name)
          ).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).filter(name => 
          ['Get Users', 'Get User', 'Get User Stats', 'Get Departments', 'Get Roles'].includes(name)
        ).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllProfileEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints профиля
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetMyProfile}
              disabled={loading}
            >
              Тест Get My Profile
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetMySettings}
              disabled={loading}
            >
              Тест Get My Settings
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetMyActivity}
              disabled={loading}
            >
              Тест Get My Activity
            </Button>
          </Grid>
        </Grid>

        {/* Отображение результатов для вкладки профиля */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(results).filter(([name]) => 
            ['Get My Profile', 'Get My Settings', 'Get My Activity'].includes(name)
          ).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).filter(name => 
          ['Get My Profile', 'Get My Settings', 'Get My Activity'].includes(name)
        ).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testSearchUsers}
              disabled={loading}
            >
              Тест Search Users
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetDepartments}
              disabled={loading}
            >
              Тест Get Departments
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetRoles}
              disabled={loading}
            >
              Тест Get Roles
            </Button>
          </Grid>
        </Grid>

        {/* Отображение результатов для вкладки поиска и статистики */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(results).filter(([name]) => 
            ['Search Users', 'Get Departments', 'Get Roles'].includes(name)
          ).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).filter(name => 
          ['Search Users', 'Get Departments', 'Get Roles'].includes(name)
        ).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllExportEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints экспорта
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testExportUsersCsv}
              disabled={loading}
            >
              Тест Export CSV
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testExportUsersJson}
              disabled={loading}
            >
              Тест Export JSON
            </Button>
          </Grid>
        </Grid>

        {/* Отображение результатов для вкладки экспорта/импорта */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(results).filter(([name]) => 
            ['Export Users CSV', 'Export Users JSON'].includes(name)
          ).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).filter(name => 
          ['Export Users CSV', 'Export Users JSON'].includes(name)
        ).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={clearResults}
            disabled={loading}
          >
            Очистить результаты
          </Button>
        </Box>

        <Grid container spacing={2}>
          {Object.entries(results).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>
    </Box>
  );
};

export default UsersApiTest;
